import { useState } from 'react';
import {
  PLACEMENT_VALUE_FIELDS,
  PLACEMENT_VALUE_MODES,
  parsePlacementAdjustmentRows,
  serializePlacementAdjustments,
  type BuilderConditionGroup,
} from '@/components/automation/automationTypes';

export function PlacementValueActionFields({
  action,
  disableTopOfSearch,
  onChange,
}: {
  action: BuilderConditionGroup['action'];
  disableTopOfSearch: boolean;
  onChange: (next: BuilderConditionGroup['action']) => void;
}) {
  const [rows, setRows] = useState(() => parsePlacementAdjustmentRows(action));

  const updateRow = (id: string, patch: { mode?: string; value?: string }) => {
    const nextRows = {
      ...rows,
      [id]: { ...rows[id], ...patch },
    };
    setRows(nextRows);
    onChange({
      type: 'placement_value',
      adjustments: serializePlacementAdjustments(nextRows, { disableTopOfSearch }),
    });
  };

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium text-foreground">Then take this action</div>
      <div className="grid gap-3 md:grid-cols-[minmax(10rem,14rem)_minmax(0,1fr)] md:items-start">
        <label className="block text-sm">
          <span className="mb-1 block text-foreground-muted">
            Action Type <span className="text-red-500">*</span>
          </span>
          <select
            className="w-full rounded border border-border px-2 py-1.5 text-sm"
            value="placement_value"
            onChange={() => undefined}
          >
            <option value="placement_value">Placement Value</option>
          </select>
        </label>
        <div className="space-y-2">
          {PLACEMENT_VALUE_FIELDS.map((field) => {
            const blocked = disableTopOfSearch && field.id === 'TOP_OF_SEARCH';
            const row = rows[field.id];
            return (
              <div
                key={field.id}
                className="grid grid-cols-[minmax(0,8.5rem)_minmax(0,1fr)_minmax(0,6.5rem)] items-center gap-2"
              >
                <span className="text-sm text-foreground">{field.label}:</span>
                <select
                  className="min-w-0 rounded border border-border px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                  value={row?.mode || 'SET'}
                  disabled={blocked}
                  aria-label={`${field.label} adjustment`}
                  onChange={(e) => updateRow(field.id, { mode: e.target.value })}
                >
                  {PLACEMENT_VALUE_MODES.map((mode) => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  className="min-w-0 rounded border border-border px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Value"
                  value={row?.value ?? ''}
                  disabled={blocked}
                  aria-label={`${field.label} value`}
                  onChange={(e) => updateRow(field.id, { value: e.target.value })}
                />
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-xs text-foreground-muted">
        If trigger conditions are met, action will get executed automatically. You can track alerts
        in executed automation alerts tab.
      </p>
    </div>
  );
}
